/**
 * Damage dans le HUD :
 * - bande de nuit dans la carte de l'horloge (dameuses en piste, avancement) ;
 * - section « Damage » de la fiche piste (velours, bosses, nuit dernière et
 *   prochaine, plan de damage) ;
 * - flotte d'un garage, fiche d'une dameuse (suivi caméra) ;
 * - panneau « Damage de nuit » (N) : besoin et capacité de la flotte, gazole,
 *   plan piste par piste, bilan de la nuit.
 */
import type { ComponentChildren } from 'preact';
import { Fuel, LocateFixed, Moon, Tractor, Warehouse, X } from 'lucide-preact';
import {
  DIFFICULTY_LABEL,
  GROOMER_STATUS_LABEL,
  GROOMING,
  NIGHT_MINUTE,
  SNOW_SURFACE_LABEL,
  buildingName,
  dayIndex,
  formatHour,
  garageCapacity,
  groomHours,
  groomProgress,
  groomerName,
  minuteOfDay,
  moguls,
  snowSurface,
  type Building,
  type Groomer,
  type Piste,
} from '@snowchain/shared';
import { Button } from './components';
import { command, focusEntity, hud } from './context';
import { euros, n0 } from './format';
import { DIFF_COLOR } from './labels';
import { Fact, Medal } from './game-ui';
import { ui } from './ui-state';
import { groomerSpots } from '../render/groomers';
import './grooming.css';

/** Heures de dameuse lisibles : « 2 h 10 », « 45 min ». */
function hours(h: number): string {
  const m = Math.round(h * 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
}

/** Nuit en cours : jour de la soirée (après minuit, c'est encore la nuit de la veille). */
function nightOf(t: number): number {
  return minuteOfDay(t) >= 12 * 60 ? dayIndex(t) : dayIndex(t) - 1;
}

/** Avancement d'une dameuse sur sa piste (0..1, deux moitiés). */
function pisteShare(g: Groomer, t: number): number {
  const pr = groomProgress(g, t);
  if (!pr) return 0;
  const partial = g.halves === 0 ? Math.abs(pr.left) : g.halves === 1 ? Math.abs(pr.right) : 1;
  return Math.min(1, (g.halves + (g.halves < 2 ? partial : 0)) / 2);
}

function statusText(g: Groomer, pistes: Map<number, Piste>, t: number): string {
  const p = g.pisteId !== null ? pistes.get(g.pisteId) : undefined;
  if (g.status === 'grooming' && p) return `${p.name} · ${n0(pisteShare(g, t) * 100)} %`;
  if (g.status === 'driving' && p) return `Vers ${p.name}`;
  return GROOMER_STATUS_LABEL[g.status];
}

const STATUS_COLOR: Record<Groomer['status'], string> = {
  parked: 'var(--ink-3)',
  driving: 'var(--glacier-3)',
  grooming: 'var(--ember-3)',
  returning: 'var(--glacier-2)',
};

/** Sélectionne une dameuse et centre la caméra dessus. */
export function selectGroomer(id: number, follow = false): void {
  const spot = groomerSpots.get(id);
  if (spot) hud.engine?.cam.focus(spot[0], spot[1], 260);
  ui.selection.value = { kind: 'groomer', id };
  ui.follow.value = follow ? id : null;
}

function GroomerRow(props: { g: Groomer; pistes: Map<number, Piste>; t: number; suffix?: ComponentChildren }) {
  const g = props.g;
  return (
    <button type="button" class="g-mini-row" onClick={() => selectGroomer(g.id)}>
      <span class="g-live-dot" style={{ background: STATUS_COLOR[g.status] }} />
      <span class="g-live-name">
        {groomerName(g)}
        {props.suffix}
      </span>
      <span class="g-live-val">{statusText(g, props.pistes, props.t)}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Carte de l'horloge                                                   */
/* ------------------------------------------------------------------ */

/** Bande « Damage » de la carte de l'horloge, tant qu'une dameuse est dehors. */
export function GroomingStrip() {
  const store = hud.store;
  const r = store?.replica;
  if (!store || !r) return null;
  const fleet = [...r.groomers.values()];
  const out = fleet.filter((g) => g.status !== 'parked');
  if (!out.length) return null;
  const t = store.clock.gameNow();
  const planned = r.me?.grooming?.planned.length ?? fleet.reduce((n, g) => n + g.done.length + g.plan.length, 0);
  const done = r.me?.grooming?.groomed.length ?? fleet.reduce((n, g) => n + g.done.length, 0);
  const progress = planned ? Math.min(1, (done + out.reduce((s, g) => s + (g.pisteId !== null ? pisteShare(g, t) : 0), 0)) / planned) : 0;
  return (
    <button type="button" class="g-groomstrip" onClick={() => (ui.grooming.value = true)} title="Damage de nuit (N)">
      <Tractor size={13} />
      <span>
        Damage · {out.length} dameuse{out.length > 1 ? 's' : ''} dehors
      </span>
      <span class="num">
        {done}/{planned} piste{planned > 1 ? 's' : ''}
      </span>
      <span class="g-groomstrip-bar">
        <span style={{ width: `${progress * 100}%` }} />
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Fiche piste                                                          */
/* ------------------------------------------------------------------ */

/** État du damage d'une piste pour la nuit en cours ou la dernière nuit. */
function pisteNight(p: Piste, t: number): { label: string; tone?: 'ok' | 'warn' | 'danger' } {
  const r = hud.store!.replica!;
  const g = [...r.groomers.values()].find((x) => x.pisteId === p.id && x.status !== 'parked');
  if (g) return { label: g.status === 'grooming' ? `${groomerName(g)} · ${n0(pisteShare(g, t) * 100)} %` : `${groomerName(g)} en route`, tone: 'ok' };
  const report = r.me?.grooming;
  const when = report && report.night === nightOf(t) ? 'cette nuit' : 'la nuit dernière';
  if (report?.groomed.includes(p.id)) return { label: `Damée ${when}`, tone: 'ok' };
  if (report?.missed.includes(p.id)) return { label: `Non damée ${when}`, tone: 'warn' };
  if (report && report.endedAt === null && report.planned.includes(p.id)) return { label: 'En attente cette nuit' };
  if (!p.groom) return { label: 'Laissée en bosses' };
  return { label: 'Non damée récemment' };
}

export function PisteGrooming(props: { piste: Piste }) {
  const store = hud.store!;
  const p = props.piste;
  const t = store.clock.gameNow();
  const surface = snowSurface(p);
  const track = store.pisteTrack(p.id);
  const h = track ? groomHours(track.length, p.width) : 0;
  const night = pisteNight(p, t);
  const bumps = moguls(p);
  const g = [...store.replica!.groomers.values()].find((x) => x.pisteId === p.id && x.status !== 'parked');
  return (
    <div class="g-groom">
      <div class="g-section-title">
        <span>
          <Tractor size={12} /> Damage
        </span>
        <span class={`g-surface g-surface-${surface}`}>{SNOW_SURFACE_LABEL[surface]}</span>
      </div>
      <div class="g-facts">
        <Fact label="Velours" value={`${n0(p.groomed * 100)} %`} title="Fraîcheur du damage : il s'use à chaque descente et sous la neige fraîche" />
        {bumps >= 0.1 && <Fact label="Bosses" value={`${n0(bumps * 100)} %`} tone={bumps >= 0.5 ? 'warn' : undefined} title="Gênent les débutants ; les experts les aiment sur les pistes raides laissées en bosses" />}
        <Fact label="Damage" value={night.label} tone={night.tone} />
        <Fact label="Prochaine nuit" value={p.groom ? `${hours(h)} · ${n0(h * GROOMING.fuelPerHour)} L` : 'Laissée en bosses'} title="Durée de damage d'une dameuse (deux passages) et gazole" />
      </div>
      {(store.isOwner || g) && (
        <div class="kh-actions">
          {store.isOwner && (
            <Button size="sm" variant={p.groom ? 'secondary' : 'primary'} onClick={() => void command({ type: 'setPisteGrooming', id: p.id, on: !p.groom }, p.groom ? `${p.name} laissée en bosses` : `${p.name} au plan de damage`)}>
              <Tractor size={14} /> {p.groom ? 'Laisser en bosses' : 'Damer chaque nuit'}
            </Button>
          )}
          {g && (
            <Button size="sm" variant="ghost" onClick={() => selectGroomer(g.id, true)}>
              <LocateFixed size={14} /> Suivre la dameuse
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Garage et dameuse                                                    */
/* ------------------------------------------------------------------ */

/** Flotte d'un garage (fiche du bâtiment). */
export function GarageFleet(props: { b: Building }) {
  const store = hud.store!;
  const r = store.replica!;
  const b = props.b;
  if (!garageCapacity(b.kind)) return null;
  const t = store.clock.gameNow();
  const fleet = [...r.groomers.values()].filter((g) => g.garageId === b.id).sort((a, c) => a.n - c.n);
  return (
    <div class="g-groom">
      <div class="g-section-title">
        <span>Dameuses</span>
        <button
          type="button"
          class="g-link"
          onClick={() => {
            ui.selection.value = null;
            ui.grooming.value = true;
          }}
        >
          Plan de damage
        </button>
      </div>
      {fleet.length ? fleet.map((g) => <GroomerRow key={g.id} g={g} pistes={r.pistes} t={t} />) : <p class="g-empty">Les dameuses arrivent à l'ouverture du garage.</p>}
    </div>
  );
}

/** Fiche d'une dameuse sélectionnée. */
export function GroomerCard(props: { g: Groomer }) {
  const store = hud.store!;
  const r = store.replica!;
  const g = props.g;
  const t = store.clock.gameNow();
  const garage = r.buildings.get(g.garageId);
  const piste = g.pisteId !== null ? r.pistes.get(g.pisteId) : undefined;
  const following = ui.follow.value === g.id;
  const names = (ids: number[]) =>
    ids
      .map((id) => r.pistes.get(id)?.name)
      .filter(Boolean)
      .join(', ') || 'aucune';
  const close = () => {
    ui.selection.value = null;
    ui.follow.value = null;
  };
  return (
    <section class="sc-hud g-panel g-inspector">
      <div class="g-card-head">
        <Medal icon={Tractor} tone="ember" size={36} />
        <div class="g-card-head-text">
          <div class="g-card-title">{groomerName(g)}</div>
          <div class="g-sub">
            {GROOMER_STATUS_LABEL[g.status]}
            {garage ? ` · ${buildingName(garage, r.buildings.values())}` : ''}
          </div>
        </div>
        <button class="sc-ibtn sc-ibtn-sm sc-ibtn-ghost" aria-label="Fermer" onClick={close}>
          <X size={14} />
        </button>
      </div>
      <div class="sc-hud-body g-inspector-body">
        <div class="g-facts">
          {piste && (
            <Fact
              label={g.status === 'grooming' ? 'Damage en cours' : 'Piste visée'}
              value={
                <button type="button" class="g-link" onClick={() => focusEntity('piste', piste.id)}>
                  {DIFFICULTY_LABEL[piste.difficulty]} · {piste.name}
                </button>
              }
            />
          )}
          {piste && <Fact label="Avancement" value={`${n0(pisteShare(g, t) * 100)} % · ${g.halves}/2 moitiés`} />}
          <Fact label="Programme de la nuit" value={g.status === 'parked' ? 'au garage' : names(g.plan)} />
          <Fact label="Pistes damées cette nuit" value={names(g.done)} />
        </div>
        <p class="g-sub g-wrap">
          Deux passages par piste : la moitié gauche puis la moitié droite. {n0(GROOMING.fuelPerHour)} L de gazole et {euros(GROOMING.driverPerHour)} de salaire par heure dehors.
        </p>
        <div class="kh-actions">
          {g.status !== 'parked' && (
            <Button size="sm" variant={following ? 'secondary' : 'primary'} onClick={() => (ui.follow.value = following ? null : g.id)}>
              <LocateFixed size={14} /> {following ? 'Arrêter de suivre' : 'Suivre'}
            </Button>
          )}
          {garage && (
            <Button size="sm" variant="ghost" onClick={() => focusEntity('building', garage.id)}>
              <Warehouse size={14} /> Garage
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Panneau « Damage de nuit »                                           */
/* ------------------------------------------------------------------ */

/** Bilan de la nuit (en cours ou dernière). */
function NightReport() {
  const store = hud.store!;
  const r = store.replica!;
  const report = r.me?.grooming;
  if (!report) return <p class="g-empty">Pas encore de nuit de damage.</p>;
  const t = store.clock.gameNow();
  const running = report.endedAt === null;
  const when = report.night === nightOf(t) ? 'Cette nuit' : 'Nuit dernière';
  const name = (id: number) => r.pistes.get(id)?.name ?? 'piste démolie';
  return (
    <>
      <div class="g-section-title">
        <span>
          <Moon size={12} /> {when}
        </span>
        <span class="g-sub">
          {formatHour(minuteOfDay(report.startedAt))} {running ? '· en cours' : `à ${formatHour(minuteOfDay(report.endedAt as number))}`}
          {report.dawn ? ' · repasse au petit matin' : ''}
        </span>
      </div>
      <div class="g-facts">
        <Fact label="Pistes damées" value={`${report.groomed.length}/${new Set([...report.planned, ...report.missed]).size}`} tone={report.missed.length ? 'warn' : 'ok'} />
        <Fact label="Heures de dameuse" value={hours(report.hours)} />
        <Fact label="Gazole" value={`${n0(report.fuel)} L`} />
        <Fact label="Salaires" value={euros(report.cost)} />
      </div>
      {report.missed.length > 0 && (
        <p class="g-sub g-wrap g-warn">
          {report.outOfFuel ? 'Gazole épuisé' : 'Poste trop court'} : {report.missed.map(name).join(', ')} non damée{report.missed.length > 1 ? 's' : ''}.
        </p>
      )}
    </>
  );
}

export function GroomingPanel() {
  void ui.tick.value;
  const store = hud.store;
  const r = store?.replica;
  if (!store || !r) return null;
  const t = store.clock.gameNow();
  const owner = store.isOwner;
  const fleet = [...r.groomers.values()].sort((a, b) => a.n - b.n);
  const garages = [...r.buildings.values()].filter((b) => b.status === 'open' && garageCapacity(b.kind) > 0);
  const pistes = [...r.pistes.values()].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  const rows = pistes.map((p) => {
    const track = store.pisteTrack(p.id);
    return { p, h: track ? groomHours(track.length, p.width) : 0 };
  });
  const need = rows.reduce((s, x) => s + (x.p.groom && x.p.snowDepth >= GROOMING.minDepth ? x.h + 0.3 : 0), 0);
  const shift = (24 * 60 - NIGHT_MINUTE + GROOMING.endMinute) / 60;
  const capacity = fleet.length * shift;
  const fuel = r.me?.stocks.fuel.qty ?? null;
  const fuelNeed = Math.ceil(need * GROOMING.fuelPerHour);
  return (
    <section class="sc-hud g-panel g-inspector g-groompanel">
      <div class="g-card-head">
        <Medal icon={Tractor} tone="ember" size={36} />
        <div class="g-card-head-text">
          <div class="g-card-title">Damage de nuit</div>
          <div class="g-sub">
            {fleet.length} dameuse{fleet.length > 1 ? 's' : ''} · {garages.length} garage{garages.length > 1 ? 's' : ''}
          </div>
        </div>
        <button class="sc-ibtn sc-ibtn-sm sc-ibtn-ghost" aria-label="Fermer" onClick={() => (ui.grooming.value = false)}>
          <X size={14} />
        </button>
      </div>
      <div class="sc-hud-body g-inspector-body">
        {!fleet.length && <p class="g-empty">Aucune dameuse : construisez un garage à dameuses (Exploitation du domaine). Sans damage, les pistes se creusent de bosses.</p>}
        {owner && <NightReport />}
        <div class="g-section-title">
          <span>Plan de damage</span>
        </div>
        <div class="g-facts">
          <Fact label="Départ" value={`dès le domaine vide, après ${formatHour(NIGHT_MINUTE)}`} />
          <Fact label="Besoin d'une nuit" value={`${hours(need)} de dameuse`} tone={need > capacity ? 'danger' : undefined} title="Deux passages par piste prévue, trajets compris" />
          <Fact label="Capacité de la flotte" value={`${hours(capacity)} (jusqu'à ${formatHour(GROOMING.endMinute)})`} />
          {fuel !== null && (
            <Fact
              label="Gazole pour une nuit"
              value={
                <>
                  <Fuel size={11} /> ≈ {n0(fuelNeed)} L · {n0(fuel)} L en stock
                </>
              }
              tone={fuel < fuelNeed ? 'warn' : undefined}
            />
          )}
          <Fact label="Salaires pour une nuit" value={`≈ ${euros(need * GROOMING.driverPerHour)}`} />
        </div>
        <div class="g-groomlist">
          {rows.map(({ p, h }) => {
            const surface = snowSurface(p);
            const night = pisteNight(p, t);
            return (
              <div class="g-groomrow" key={p.id}>
                <span class="g-live-dot" style={{ background: DIFF_COLOR[p.difficulty] }} />
                <button type="button" class="g-groomrow-name" onClick={() => focusEntity('piste', p.id)} title={`${DIFFICULTY_LABEL[p.difficulty]} · ${n0(p.length)} m · ${hours(h)} de damage`}>
                  {p.name}
                  <span class="g-sub">{night.label}</span>
                </button>
                <span class={`g-surface g-surface-${surface}`}>{SNOW_SURFACE_LABEL[surface]}</span>
                {owner ? (
                  <button
                    type="button"
                    class={`g-groomtoggle${p.groom ? ' on' : ''}`}
                    aria-pressed={p.groom}
                    title={p.groom ? 'Damée chaque nuit : cliquer pour la laisser en bosses' : 'Laissée en bosses : cliquer pour la damer chaque nuit'}
                    onClick={() => void command({ type: 'setPisteGrooming', id: p.id, on: !p.groom })}
                  >
                    {p.groom ? 'Chaque nuit' : 'Bosses'}
                  </button>
                ) : (
                  <span class="g-sub">{p.groom ? 'chaque nuit' : 'bosses'}</span>
                )}
              </div>
            );
          })}
        </div>
        {fleet.length > 0 && (
          <>
            <div class="g-section-title">
              <span>Flotte</span>
            </div>
            {fleet.map((g) => (
              <GroomerRow key={g.id} g={g} pistes={r.pistes} t={t} />
            ))}
          </>
        )}
      </div>
    </section>
  );
}